import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useState } from "react"
import { CountryDropdownField } from "../country-select/country-select"
import TSLCandidate from "../tsl-candidate/tsl-candidate"

import { formField, formFieldError } from "./dynamic-field.module.css"
import { trackEvent } from "../../modules/analytics"

const DynamicField = ({
  fieldData,
  values,
  submitted,
  handleChange,
  initialValues,
  formId,
  style,
  theme,
  errors,
}) => {
  const [dirtyFields, setDirtyFields] = useState([])

  const renderMarkdownLink = (text) => {
    const regex = /\[([^\]]+)\]\(([^")]+)(?:\s"([^"]+)")?\)/g

    const replaceLinks = (match, text, url) => {
      return `<a href=${url} target="_blank" rel="noopener noreferrer" title=${text}>
          ${text}
        </a>`
    }

    const markdownText = text.replace(regex, replaceLinks)

    return <span dangerouslySetInnerHTML={{ __html: markdownText }} />
  }

  const handleFieldChange = (fieldName, value) => {
    if (dirtyFields.includes(fieldName)) return

    // Check if field is dirty
    if (initialValues[fieldName] !== value) {
      trackEvent(`form_field_${fieldName.substring(0, 22)}`, {
        form_id: formId,
      })
      setDirtyFields((prevState) => [...prevState, fieldName])
    }
  }

  const handleCandidateFieldChange = (candidateIndex, fieldName, value) => {
    const candidateFieldName = `users.${candidateIndex}.${fieldName}`
    if (dirtyFields.includes(candidateFieldName)) return

    // Check if field is dirty
    if (initialValues[candidateFieldName] !== value) {
      trackEvent(
        `form_field_cand${candidateIndex}_${fieldName.substring(0, 13)}`,
        {
          form_id: formId,
        }
      )
      setDirtyFields((prevState) => [...prevState, candidateFieldName])
    }
  }

  // const handleCandidateFieldChange = (candidateIndex, fieldName, value) => {
  //   const candidateFieldName = `users.${candidateIndex}.${fieldName}`
  //   if (dirtyFields.includes(candidateFieldName)) return

  //   // Check if field is dirty
  //   if (initialValues[candidateFieldName] !== value) {
  //     analyticsTrackEvent(
  //       `form${formIndex}_field_cand${candidateIndex}_${fieldName.substring(
  //         0,
  //         15
  //       )}`
  //     )
  //     setDirtyFields((prevState) => [...prevState, candidateFieldName])
  //   }
  // }

  const fieldName = fieldData.slug !== "" ? fieldData.slug : fieldData.id
  switch (fieldData.type) {
    case "text":
      return (
        <>
          <label htmlFor={fieldName} className={style.formFieldLabel}>
            <span>{renderMarkdownLink(fieldData.label)}</span>
            {fieldData.required && <span style={{ color: "gray" }}>*</span>}
          </label>
          <Field
            className={style.formField}
            validate={(value) =>
              fieldData.required && !value ? `Dit veld is verplicht` : undefined
            }
            placeholder={fieldData.placeholder}
            type={fieldData.type}
            name={fieldName}
            onChange={(e) => {
              handleChange(e)
              handleFieldChange(fieldName, e.target.value)
            }}
          />
          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    case "textarea":
      return (
        <>
          <label htmlFor={fieldName} className={style.formFieldLabel}>
            <span>{renderMarkdownLink(fieldData.label)}</span>
            {fieldData.required && <span style={{ color: "gray" }}>*</span>}
          </label>
          <Field
            className={style.formField}
            as="textarea"
            validate={(value) =>
              fieldData.required && !value ? `Dit veld is verplicht` : undefined
            }
            placeholder={fieldData.placeholder}
            type={fieldData.type}
            name={fieldName}
            onChange={(e) => {
              handleChange(e)
              handleFieldChange(fieldName, e.target.value)
            }}
          />
          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    case "checkbox":
      return (
        <>
          <label htmlFor={fieldName} className={style.formCheckboxLabel}>
            <Field
              className={style.formCheckbox}
              validate={(value) =>
                fieldData.required && !value
                  ? `Dit veld is verplicht`
                  : undefined
              }
              style={{ height: "unset" }}
              type={fieldData.type}
              name={fieldName}
              onChange={(e) => {
                handleChange(e)
                console.log(e.target.value)
                handleFieldChange(fieldName, e.target.value)
              }}
            />
            {renderMarkdownLink(fieldData.label)}
          </label>

          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    case "select":
      // TODO: Add analytics
      return (
        <>
          <label htmlFor={fieldName}>
            {fieldData.label}
            {fieldData.required && <span style={{ color: "gray" }}>*</span>}
          </label>

          <Field
            as="select"
            className={style.formField}
            name={fieldName}
            id={fieldName}
          >
            <option disabled={fieldData.required} selected value="">
              Kies een optie
            </option>
            {fieldData.options.map((selectItem) => (
              <option key={selectItem} value={selectItem}>
                {selectItem}
              </option>
            ))}
          </Field>

          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    case "radio":
      return (
        <>
          <label htmlFor={fieldName}>
            {fieldData.label}
            {fieldData.required && <span style={{ color: "gray" }}>*</span>}
          </label>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {fieldData.multiple
              ? fieldData.options.map((checkboxItem) => (
                  <div key={checkboxItem} className={style.checkboxOption}>
                    <label htmlFor={`${fieldName}-${checkboxItem}`}>
                      <Field
                        type="checkbox"
                        name={fieldName}
                        value={checkboxItem}
                        id={`${fieldName}-${checkboxItem}`}
                        className={style.formCheckbox}
                        style={{ marginRight: "6px" }}
                      />
                      {checkboxItem}
                    </label>
                  </div>
                ))
              : fieldData.options.map((radioItem) => (
                  <div key={radioItem} className={style.radioOption}>
                    <label htmlFor={`${fieldName}-${radioItem}`}>
                      <Field
                        type="radio"
                        name={fieldName}
                        value={radioItem}
                        id={`${fieldName}-${radioItem}`}
                        className={style.formRadio}
                        style={{ marginRight: "6px" }}
                      />
                      {radioItem}
                    </label>
                  </div>
                ))}
          </div>

          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    // CUSTOM TYPES
    case "countryselect":
      const downloadStyle = {
        margin: 0,
        width: "100%",
        boxSizing: "border-box",
        borderRadius: "5px",
        border: "2px solid #c9c9c9",
        height: "44px",
        alignSelf: "stretch",
        paddingLeft: "10px",
        fontSize: "16px !important",
        fontFamily: '"Quicksand", sans-serif, Arial, Helvetica',
        fontWeight: 700,
      }

      return (
        <>
          <label htmlFor={fieldName}>
            <span>{renderMarkdownLink(fieldData.label)}</span>
            {fieldData.required && <span style={{ color: "gray" }}>*</span>}
          </label>
          <CountryDropdownField
            name={fieldName}
            isRequired={fieldData.required}
            style={theme === "download" ? downloadStyle : undefined}
            onChange={(e) => {
              handleChange(e)
              handleFieldChange(fieldName, e.target.value)
            }}
          />

          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    case "addcandidate":
      return (
        <>
          <TSLCandidate
            fieldName={fieldName}
            fieldData={fieldData}
            values={values}
            submitted={submitted}
            errors={errors}
            onChange={(e, index, fieldName) => {
              handleChange(e)
              handleCandidateFieldChange(index, fieldName, e.target.value)
            }}
          />

          <ErrorMessage
            className={`${formFieldError} download-form-field-error`}
            name={fieldName}
            component="div"
          />
        </>
      )
    default:
      return <Field />
  }
}

export default DynamicField
